import { useState, useCallback } from 'react';

export interface StarRatingProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  error?: string;
  required?: boolean;
}

const sizeClasses = {
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

const StarIcon = ({
  filled,
  hovered,
  size,
}: {
  filled: boolean;
  hovered: boolean;
  size: 'sm' | 'md' | 'lg';
}) => (
  <svg
    className={`${sizeClasses[size]} transition-colors duration-150`}
    fill={filled || hovered ? 'currentColor' : 'none'}
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);

export const StarRating = ({
  value,
  onChange,
  label,
  disabled = false,
  size = 'md',
  className = '',
  error,
  required = false,
}: StarRatingProps) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const handleMouseEnter = useCallback(
    (star: number) => {
      if (!disabled) {
        setHoverValue(star);
      }
    },
    [disabled]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverValue(null);
  }, []);

  const handleClick = useCallback(
    (star: number) => {
      if (!disabled) {
        onChange(star);
      }
    },
    [disabled, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, star: number) => {
      if (disabled) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(star);
      } else if (e.key === 'ArrowRight' && value < 5) {
        e.preventDefault();
        onChange(value + 1);
      } else if (e.key === 'ArrowLeft' && value > 1) {
        e.preventDefault();
        onChange(value - 1);
      }
    },
    [disabled, onChange, value]
  );

  const displayValue = hoverValue ?? value;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div
        className="flex items-center gap-1"
        role="radiogroup"
        aria-label={label ?? 'Star rating'}
        onMouseLeave={handleMouseLeave}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            disabled={disabled}
            className={`
              ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              ${star <= displayValue ? 'text-yellow-400' : 'text-gray-300'}
              hover:scale-110 transition-transform duration-150
              focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-1 rounded
            `}
            onClick={() => handleClick(star)}
            onMouseEnter={() => handleMouseEnter(star)}
            onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, star)}
          >
            <StarIcon
              filled={star <= displayValue}
              hovered={hoverValue !== null && star <= hoverValue}
              size={size}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-600" aria-live="polite">
          {displayValue > 0 ? `${displayValue} of 5` : 'Not rated'}
        </span>
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
